import React, { Fragment, useEffect, useContext, useState } from "react";
import { Card, Button, Form, Col, Collapse, Row, Alert } from "react-bootstrap";
import { abpnClient } from "../../../Services/AbpnClient";
import { AuthContext } from "../../Auth/AuthProvider";
import { Certificate, CertificateStatus } from "../../Physician/Models/Certificate";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import ApplicationModel from "../Models/ApplicationModel";
import { ExaminationModule, KnownCertifications, ModuleType } from "../Models/Examination";
import CnpOptions from "./CnpOptions";

interface IProps {
    isSectionOpen: boolean;
    modules: Array<ExaminationModule>;
    onContinue: (moduleCertificationKeys: Array<number>, cnpType: number) => void;
    moduleTypes: Array<ModuleType>;
    certificates: Array<Certificate>;
    examCertificationIds: Array<number>;
    application: ApplicationModel;
    onOpen: () => void;
    disabled: boolean;
    isLoading?: boolean;
}

export function Modules(props: IProps) {
    const physicianInactivePrimaryCertIds = props.certificates.filter(_ => _.isPrimary && _.certificateStatus === CertificateStatus.Inactive)?.map(_ => _.certificationId);

    const isCertificationAvailable = (certificationId: number) => {
        return availableCertificationIds.some(_ => _ == certificationId);
    }

    const isCertificationIdRequiredModuleType = (certificationId: number) => {
        return physicianInactivePrimaryCertIds.some(_ => _ == certificationId);
    }

    const isExistingSelectedModuleType = (certificationId: number) => {
        return props.application.modularCertificationIds?.some(_ => _ == certificationId);
    }

    const filterAvailableModules = () => {
        var availablePhysicianModules = props.moduleTypes.filter(_ => isCertificationAvailable(_.certificationId));
        var existingSelectedModules = props.moduleTypes.filter(_ => isExistingSelectedModuleType(_.certificationId));
        var availableModules = availablePhysicianModules.concat(existingSelectedModules);
        var filteredExamModules = availableModules.filter(_ => props.examCertificationIds.includes(Number(_.certificationId)));

        return props.modules.filter(_ => filteredExamModules.some(x => x.moduleTypeId == _.moduleTypeId));
    }

    const requiredCertificationIds = props.moduleTypes.filter(_ => isCertificationIdRequiredModuleType(_.certificationId))?.map(_ => _.certificationId);

    const [moduleSelections, setModuleSelections] = useState<Array<number>>([]);
    const [availableCertificationIds, setAvailableCertificationIds] = useState<Array<number>>([]);
    const [cnpType, setCnpType] = useState<number>(props.application.cnpType);
    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [errorMessages, setErrorMessages] = useState([]);

    const authContext = useContext(AuthContext);

    useEffect(() => {
        const setDefaultModuleSelections = () => {
            if (props.application.modularCertificationIds !== null) {
                setModuleSelections(props.application.modularCertificationIds);
                return;
            } 

            if (requiredCertificationIds !== null) setModuleSelections(requiredCertificationIds);
        }

        const getAvailableCertificationIds = () => {
            abpnClient.get<Array<number>>("/api/application/availableModules", authContext).then(result => {
                setAvailableCertificationIds(result);
            })
        }
        setDefaultModuleSelections();
        getAvailableCertificationIds();
    }, []);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (validateSelection(moduleSelections)) {
            props.onContinue(moduleSelections, cnpType);
        }
    }

    const validateSelection = (selections: number[]) => {
        let newErrorMessages = [];

        if (selections.length <= 1 || selections.length >= 4) {
            newErrorMessages.push("You must select 2 or 3 certifications");
        }

        const isCnpSelected = selections.some(certificationId => certificationId == KnownCertifications.ClinicalNeurophysiology);

        if (isCnpSelected && !cnpType) {
            newErrorMessages.push("You must select a CNP focus if you've selected the CNP module");
        }

        setErrorMessages(newErrorMessages);
        return newErrorMessages.length === 0;
    }

    const updateModuleSelection = (certificationId: number) => {
        var updated = deepCopy(moduleSelections);
        var index = updated.indexOf(certificationId);

        if (index >= 0) {
            updated.splice(index, 1);
            if (cnpType && certificationId === KnownCertifications.ClinicalNeurophysiology) {
                setCnpType(null);
            }
            setModuleSelections(updated);
            return;
        }
        updated.push(certificationId);
        setModuleSelections(updated);
    }

    const updateCnpType = (updatedCnpType: number) => {
        setCnpType(updatedCnpType);

        var index = moduleSelections.indexOf(KnownCertifications.ClinicalNeurophysiology);
        if ((updatedCnpType && index < 0) || (!updatedCnpType && index >= 0)) {
            updateModuleSelection(KnownCertifications.ClinicalNeurophysiology);
        }
    }

    const isChecked = (module: ExaminationModule) => {
        if (isRequired(module)) return true;

        var certificationId = props.moduleTypes.find(_ => _.moduleTypeId == module.moduleTypeId).certificationId
        return moduleSelections.some(_ => _ == certificationId);
    }

    const isRequired = (module: ExaminationModule) => {
        var certificationId = props.moduleTypes.find(_ => _.moduleTypeId == module.moduleTypeId).certificationId;
        return requiredCertificationIds.some(_ => _ == certificationId);
    }

    const isCnp = (module: ExaminationModule) => {
        return props.moduleTypes.some(_ => _.moduleTypeId == module.moduleTypeId && _.certificationId == KnownCertifications.ClinicalNeurophysiology);
    }

    const getCertificate = (moduleTypeId: number) => {
        var moduleType = props.moduleTypes.find(_ => _.moduleTypeId === moduleTypeId);
        return props.certificates.find(_ => _.certificationId == moduleType.certificationId);
    }

    useEffect(() => {
        setOpenCollapse(props.isSectionOpen);
    }, [props.isSectionOpen]);

    useEffect(() => {
        if (openCollapse) props.onOpen();
    }, [openCollapse]);

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder" onClick={() => { if (!props.disabled) setOpenCollapse(!openCollapse) }}>
                    <span className={`text-pp-primary ${props.disabled && 'pp-disabled'}`}><i className="font-weight-bolder las la-angle-down"></i> Certifications</span>
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Row className="mt-3">
                        <Col xs={12} md={8} lg={6} xl={6}>
                            <Form noValidate onSubmit={(e) => onSubmit(e)}>
                                <Form.Group controlId="formCertificationsControls">
                                    <Form.Text className="mb-3 font-weight-bold">Select two or three certifications to include in your examination
                                        <span className="text-pp-primary ml-2">*</span>
                                    </Form.Text>
                                    {filterAvailableModules().map(module => {
                                        return (
                                            <Fragment>
                                                <Form.Check type="checkbox">
                                                    <Form.Check.Input type="checkbox"
                                                        value={getCertificate(module.moduleTypeId).certificationId}
                                                        checked={isChecked(module)}
                                                        disabled={isRequired(module)}
                                                        id={module.examinationModuleId.toString()}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateModuleSelection(parseInt(e.target.value))} />
                                                    <Form.Check.Label className="mb-1 pp-font-size-smaller"> {getCertificate(module.moduleTypeId).certificationName}</Form.Check.Label>
                                                </Form.Check>
                                                {isCnp(module) &&
                                                    <div className="ml-4">
                                                        <CnpOptions cnpType={cnpType} cnpTypeUpdated={updateCnpType} />
                                                    </div>
                                                }
                                            </Fragment>
                                        )
                                    })}
                                </Form.Group>
                                {errorMessages.length > 0 &&
                                    <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                        {errorMessages.map(errorMessage => <p>{errorMessage}</p>)}
                                    </Alert>
                                }
                                {props.isLoading ? <AbpnSpinner /> : <Button type="submit" variant="pp-primary">Continue</Button>}
                            </Form>
                        </Col>
                    </Row>
                </Collapse>
            </Card.Body>
        </Card>
    );
}
export default Modules;